/**
 * Runtime-validated environment variables.
 *
 * Zod parses once at module load. Importing anywhere gives a fully-typed
 * object; bad config fails fast on the server boot, not deep in a request.
 *
 * Anything exposed to the browser must be prefixed `NEXT_PUBLIC_` (Next.js
 * bundles only those into client code). Server-only secrets are read here
 * but never shipped to the client.
 */

import { z } from "zod";

import { APP_ENVS } from "@/lib/app-envs";

const serverSchema = z.object({
  // `dual` runs both Credentials and Entra providers at once during the
  // Phase 4 cutover window (ADR-031). Sunset back to `entra`-only when the
  // criteria in ADR-031 are met.
  AUTH_MODE: z.enum(["mock", "dual", "entra"]).default("mock"),
  AUTH_SECRET: z.string().min(1).default("dev-secret-change-me"),
  // Staging-grade email+password pairs for the mock Credentials provider.
  // Format: `email:password,email:password`. Server-only; never NEXT_PUBLIC_.
  AUTH_USERS: z.string().default(""),
  // Server-only whitelist applied as defense-in-depth in the signIn callback.
  // Per ADR-031, gates BOTH the Credentials and Entra paths when populated.
  // Empty value disables the check (dev convenience).
  ALLOWED_EMAILS: z.string().default(""),
  // Kearney tenant ID — checked against `profile.tid` on Entra sign-ins
  // (ADR-031). Required for `dual`/`entra` modes; if unset, Entra sign-ins
  // are rejected by the callback. Not used in `mock` mode.
  AUTH_KEARNEY_TENANT_ID: z.string().optional(),
  // Object IDs of the AD security groups that gate access to the app.
  // `predictive-k-users` = read-only access, `predictive-k-admins` = admin
  // features. When `AUTH_KEARNEY_USER_GROUP_ID` is set, group membership
  // is the authoritative authorization gate on the Entra path; the
  // `ALLOWED_EMAILS` fallback only kicks in while these are unset (so
  // sign-in keeps working between cert install and group ID delivery).
  AUTH_KEARNEY_USER_GROUP_ID: z.string().optional(),
  AUTH_KEARNEY_ADMIN_GROUP_ID: z.string().optional(),
  // Deployment environment label. The dev-admin-emails fallback (below)
  // ONLY activates when this is exactly ``"dev"``. Empty/unset = no
  // fallback, so qa/prod/demo never honor the override even if the var is
  // mistakenly populated.
  ENVIRONMENT: z.enum(APP_ENVS).optional(),
  // CSV of emails granted admin in dev when the Entra ``groups`` claim is
  // missing (App Registration not configured to emit ``SecurityGroup``).
  // Only consulted when ``ENVIRONMENT=dev`` AND the Entra signIn would
  // otherwise be denied for lack of groups. NEVER read in qa/prod.
  AUTH_DEV_ADMIN_EMAILS: z.string().default(""),
  // CSV of email domains allowed to sign in. Final post-auth gate that
  // runs only when ``ENVIRONMENT=dev`` AND the list is non-empty — in
  // qa/prod it's a no-op even if populated. Used to keep the dev
  // environment Kearney-only when the Entra security group also
  // contains FE / external members. Domains compared case-insensitive
  // (e.g. ``kearney.com`` matches ``Tony.Jonas@Kearney.com``).
  AUTH_DEV_ALLOWED_EMAIL_DOMAINS: z.string().default(""),
  // Empty or unset = let Auth.js derive the canonical URL from the
  // request Host header (AUTH_TRUST_HOST=true). Set to an absolute
  // http(s) URL only when pinning the deployment to one domain.
  NEXTAUTH_URL: z
    .union([z.string().url(), z.literal("")])
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  AUTH_MICROSOFT_ENTRA_ID_ID: z.string().optional(),
  AUTH_MICROSOFT_ENTRA_ID_SECRET: z.string().optional(),
  AUTH_MICROSOFT_ENTRA_ID_ISSUER: z.string().optional(),
});

const clientSchema = z.object({
  NEXT_PUBLIC_AUTH_MODE: z.enum(["mock", "dual", "entra"]).default("mock"),
  // PMTiles + MapLibre GL fleet-map engine (docs/pmtiles-map-implementation-plan.md
  // §8.4, ADR-049 draft). "leaflet" (default) keeps every existing fleet-map
  // screen on the Leaflet path, including the legacy `/map`,
  // `/map/heatmap`, `/map/device/{id}/detail` endpoints. "tiles" mounts
  // `MapLibreMapShell` instead.
  //
  // Default flipped to "tiles" 2026-08-04 (ADR-059 Q19). It had stayed
  // "leaflet" long after both deployed environments moved to tiles, so
  // the default contradicted reality; ADR-059 then froze the Leaflet path
  // and deleted its layer checkboxes, which turned that contradiction
  // into a real trap — a build without the dev/prod compose override
  // landed on a map with NO layer controls at all.
  //
  // Two claims in the comment this replaces were also overtaken: Circuit
  // Map is no longer flagless or "Leaflet-only permanently", and the W5.2
  // parity sign-off it gated on has been superseded by the ADR-059
  // browser verification (56/56 across all five map screens).
  //
  // "leaflet" still works and is still the rollback lever — set the env
  // var explicitly.
  NEXT_PUBLIC_MAP_ENGINE: z.enum(["tiles", "leaflet"]).default("tiles"),
  // Default map viewport (ADR-081) — parsed and documented in
  // `lib/map-view.ts`. Centre and bounds are LAT FIRST whichever map library
  // consumes them: that is what a person pastes from a map URL. Empty keeps
  // the neutral built-in (the contiguous US), never a client's territory.
  NEXT_PUBLIC_MAP_CENTER: z.string().default(""),
  NEXT_PUBLIC_MAP_BOUNDS: z.string().default(""),
  // `.catch(0)`: a typo here must fall back like centre and bounds do
  // (lib/map-view.ts reads 0 as "use the default"), not fail the whole
  // client env and with it the app.
  NEXT_PUBLIC_MAP_ZOOM: z.coerce.number().default(0).catch(0),
  // Brand and place names shown to a user (E-04a, E-04d) — resolved and
  // documented in `lib/brand.ts`. Same rule as the map territory: empty
  // keeps a neutral built-in that names no client; FE's own values are
  // declared in apps/docker-compose.yml. `APP_NAME` and
  // `CAPITAL_PROGRAM_LABEL` are the names the demo's /v1 front already
  // reads, so one set of values serves both fronts.
  NEXT_PUBLIC_APP_NAME: z.string().default(""),
  NEXT_PUBLIC_CLIENT_NAME: z.string().default(""),
  NEXT_PUBLIC_WHOLE_TERRITORY_LABEL: z.string().default(""),
  NEXT_PUBLIC_RELEASE_LABEL: z.string().default(""),
  NEXT_PUBLIC_CAPITAL_PROGRAM_LABEL: z.string().default(""),
  NEXT_PUBLIC_USE_MOCKS: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),
  // Empty (the default) routes API calls through the page's own origin —
  // Caddy proxies /api/* to the api service. Set to an absolute http(s)
  // URL only when the API is on a different origin than the web app.
  NEXT_PUBLIC_API_URL: z
    .string()
    .default("")
    .refine((v) => v === "" || /^https?:\/\//.test(v), {
      message: "NEXT_PUBLIC_API_URL must be empty or an http(s) URL",
    }),
  // Deployment environment label. Drives per-screen visibility (each
  // navigation entry / route layout can opt in to one or more envs)
  // and unifies the prod-only behaviours that used to live behind a
  // separate boolean flag:
  //   * Sidebar hides groups / leaves tagged ``envs: ["dev"]``;
  //   * ``/scenarios/*`` and ``/plans/*`` layouts 404 outside their
  //     allowed envs; home redirects to Historical Data in prod;
  //   * Scenario pickers on Impact / Prescriptive restrict to the
  //     ``actual_constraints_circuit_level`` and
  //     ``actual_constraints_company_level`` SCENARIOs when
  //     ENVIRONMENT=prod (see ScenarioPicker PROD_ALLOWED_SCENARIOS).
  // Default ``"dev"`` — flip to ``"prod"`` in the prod compose
  // before building the web image. ``"qa"`` is reserved for the
  // intermediate environment if one ever materialises.
  // ``"demo"`` is the Predictive-K sales demo (see ``lib/app-envs.ts``).
  NEXT_PUBLIC_ENVIRONMENT: z.enum(APP_ENVS).default("dev"),
});

// Client env — safe to reference in any component (server or client).
export const clientEnv = clientSchema.parse({
  NEXT_PUBLIC_AUTH_MODE: process.env.NEXT_PUBLIC_AUTH_MODE,
  NEXT_PUBLIC_USE_MOCKS: process.env.NEXT_PUBLIC_USE_MOCKS,
  NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
  NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT,
  NEXT_PUBLIC_MAP_ENGINE: process.env.NEXT_PUBLIC_MAP_ENGINE,
  NEXT_PUBLIC_MAP_CENTER: process.env.NEXT_PUBLIC_MAP_CENTER,
  NEXT_PUBLIC_MAP_BOUNDS: process.env.NEXT_PUBLIC_MAP_BOUNDS,
  NEXT_PUBLIC_MAP_ZOOM: process.env.NEXT_PUBLIC_MAP_ZOOM,
  NEXT_PUBLIC_APP_NAME: process.env.NEXT_PUBLIC_APP_NAME,
  NEXT_PUBLIC_CLIENT_NAME: process.env.NEXT_PUBLIC_CLIENT_NAME,
  NEXT_PUBLIC_WHOLE_TERRITORY_LABEL:
    process.env.NEXT_PUBLIC_WHOLE_TERRITORY_LABEL,
  NEXT_PUBLIC_RELEASE_LABEL: process.env.NEXT_PUBLIC_RELEASE_LABEL,
  NEXT_PUBLIC_CAPITAL_PROGRAM_LABEL:
    process.env.NEXT_PUBLIC_CAPITAL_PROGRAM_LABEL,
});

// Server env — importing this in a Client Component is a bug.
// We evaluate lazily so Next.js can tree-shake it out of client bundles.
export function serverEnv() {
  return serverSchema.parse({
    AUTH_MODE: process.env.AUTH_MODE,
    AUTH_SECRET: process.env.AUTH_SECRET,
    AUTH_USERS: process.env.AUTH_USERS,
    ALLOWED_EMAILS: process.env.ALLOWED_EMAILS,
    NEXTAUTH_URL: process.env.NEXTAUTH_URL,
    AUTH_MICROSOFT_ENTRA_ID_ID: process.env.AUTH_MICROSOFT_ENTRA_ID_ID,
    AUTH_MICROSOFT_ENTRA_ID_SECRET: process.env.AUTH_MICROSOFT_ENTRA_ID_SECRET,
    AUTH_MICROSOFT_ENTRA_ID_ISSUER: process.env.AUTH_MICROSOFT_ENTRA_ID_ISSUER,
    AUTH_KEARNEY_TENANT_ID: process.env.AUTH_KEARNEY_TENANT_ID,
    AUTH_KEARNEY_USER_GROUP_ID: process.env.AUTH_KEARNEY_USER_GROUP_ID,
    AUTH_KEARNEY_ADMIN_GROUP_ID: process.env.AUTH_KEARNEY_ADMIN_GROUP_ID,
    ENVIRONMENT: process.env.ENVIRONMENT,
    AUTH_DEV_ADMIN_EMAILS: process.env.AUTH_DEV_ADMIN_EMAILS,
    AUTH_DEV_ALLOWED_EMAIL_DOMAINS: process.env.AUTH_DEV_ALLOWED_EMAIL_DOMAINS,
  });
}

/**
 * Parse the server-side `ALLOWED_EMAILS` CSV into a normalized, lowercased
 * array. Used by the Entra signIn callback as defense-in-depth.
 */
export function parseAllowedEmails(raw: string): string[] {
  return raw
    .split(",")
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0);
}

/**
 * Parse the `AUTH_USERS` CSV (`email:password,email:password`) into a
 * lowercase-email → password map. Malformed entries (no colon, empty email,
 * empty password) are silently dropped. Server-only — this map must never
 * cross the network boundary.
 */
export function parseAuthUsers(raw: string): Map<string, string> {
  const users = new Map<string, string>();
  for (const pair of raw.split(",")) {
    const trimmed = pair.trim();
    if (!trimmed) continue;
    const idx = trimmed.indexOf(":");
    if (idx <= 0) continue;
    const email = trimmed.slice(0, idx).trim().toLowerCase();
    const password = trimmed.slice(idx + 1);
    if (!email || !password) continue;
    users.set(email, password);
  }
  return users;
}
